import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { KeyRound, Copy, Download, Plus, Upload, Check, Wrench, ArrowLeft, Cpu, ChevronDown, AlertTriangle, History } from 'lucide-react';
import {
  FEATURE_METADATA,
  LICENSABLE_FEATURES,
  formatHardwareId,
  isLicensableFeature,
  normalizeHardwareId,
  quoteLicense,
  type LicenseCheckoutResponse,
  type LicenseQuoteResponse,
  type PortalAppliance,
  type PortalLicense,
} from '@scada-ink/shared/cloud';
import { Button } from '@/components/ui';
import { Alert, TextArea, TextField } from '@/components/form';
import { FeaturePicker, QuoteSummary, formatUsd, selectionFromParams, type LicenseSelection } from '@/components/LicenseBuilder';
import { usePageTitle } from '@/components/layout/SiteLayout';
import { cloudApi, errorMessage } from '@/lib/cloudApi';
import { useSession } from '@/lib/session';
import { useAsync } from '@/lib/useAsync';
import { Card, EmptyState, LoadError, Loading, PortalHeader, Table, formatDate } from './components';

function downloadKey(license: PortalLicense) {
  if (!license.key) return;
  const text = [
    'Scada.Ink license key',
    `License:     ${license.label || license.id}`,
    `Hardware ID: ${formatHardwareId(license.hardwareId)}`,
    `Features:    ${license.isAll ? 'All features' : `${license.features.length} features`}`,
    `Issued:      ${license.issuedAt ?? ''}`,
    '',
    'Activate on the appliance: Admin → Settings → License → paste the key → Activate.',
    'This key works only on the appliance with the hardware ID above.',
    '',
    license.key,
    '',
  ].join('\n');
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `scada-ink-license-${license.hardwareId}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copied' : label}
    </Button>
  );
}

function LicenseCard({ license, onChanged, canEdit }: { license: PortalLicense; onChanged: () => void; canEdit: boolean }) {
  const navigate = useNavigate();
  const [showFeatures, setShowFeatures] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const licensable = license.features.filter(isLicensableFeature);
  const m = license.maintenance;
  const plan = license.edition === 'hmi' ? 'support' : 'maintenance';

  async function maintenance(action: 'start' | 'cancel') {
    if (action === 'cancel' && !window.confirm(`Cancel ${plan}? Updates and priority support end at the end of the current year. The license keeps working.`)) return;
    setBusy(true);
    setError(null);
    try {
      if (action === 'start') {
        const res = await cloudApi.post<LicenseCheckoutResponse>(`/api/portal/licenses/${encodeURIComponent(license.id)}/maintenance`);
        if (res.kind === 'redirect') {
          window.location.assign(res.url);
          return;
        }
      } else {
        await cloudApi.post(`/api/portal/licenses/${encodeURIComponent(license.id)}/maintenance/cancel`);
      }
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="glass-panel overflow-hidden">
      <div className="p-5 flex flex-col md:flex-row md:items-start gap-4 border-b border-glass-border">
        <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${license.isAll ? 'bg-navy-900 text-brand-accent' : 'bg-brand-primary/10 text-brand-primary'}`}>
          <KeyRound size={20} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-bold">{license.label || (license.applianceName ?? 'Appliance license')}</h2>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${license.isAll ? 'bg-navy-900 text-white' : 'bg-brand-primary/10 text-brand-primary'}`}>
              {license.edition === 'hmi' ? 'HMI.ink license' : license.isAll ? 'All features' : `${licensable.length} of ${LICENSABLE_FEATURES.length} features`}
            </span>
            {license.source === 'import' && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-ink-muted text-text-secondary">Imported backup</span>}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-muted">
            <span className="inline-flex items-center gap-1">
              <Cpu size={12} /> <span className="font-mono">{formatHardwareId(license.hardwareId)}</span>
            </span>
            {license.applianceName && <span>Appliance: {license.applianceName}</span>}
            <span>Issued {formatDate(license.issuedAt)}</span>
            {license.pricePaidUsd !== null && <span>Paid {formatUsd(license.pricePaidUsd)}</span>}
          </div>
        </div>
        {license.source === 'purchase' && canEdit && licensable.length < LICENSABLE_FEATURES.length && (
          <Button size="sm" onClick={() => navigate(`/portal/licenses/new?hwid=${license.hardwareId}${license.edition === 'hmi' ? '&all=0&features=' : ''}`)}>
            <Plus size={14} /> {license.edition === 'hmi' ? 'Upgrade to Scada.Ink' : 'Add features'}
          </Button>
        )}
      </div>

      <div className="p-5 space-y-4">
        {license.key ? (
          <div>
            <div className="text-xs font-semibold mb-1.5">License key</div>
            <div className="rounded-[var(--radius-glass-sm)] bg-ink-muted border border-glass-border px-3 py-2.5 font-mono text-xs break-all max-h-24 overflow-y-auto select-all">{license.key}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <CopyButton text={license.key} label="Copy key" />
              <Button variant="secondary" size="sm" onClick={() => downloadKey(license)}>
                <Download size={13} /> Download .txt
              </Button>
            </div>
            <p className="mt-2 text-xs text-text-muted">Activate on the appliance under Admin → Settings → License. It works only on hardware ID {formatHardwareId(license.hardwareId)}.</p>
          </div>
        ) : (
          <Alert kind="info">Waiting for payment confirmation. The key appears here as soon as the payment clears.</Alert>
        )}

        <button type="button" onClick={() => setShowFeatures(!showFeatures)} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-primary">
          {showFeatures ? 'Hide' : 'Show'} licensed features <ChevronDown size={13} className={showFeatures ? 'rotate-180' : ''} />
        </button>
        {showFeatures && (
          <div className="flex flex-wrap gap-1.5">
            {licensable.map((f) => (
              <span key={f} className="px-2 py-1 rounded-md bg-ink-muted text-xs">
                {FEATURE_METADATA[f].name}
              </span>
            ))}
          </div>
        )}

        {license.source === 'purchase' && (
          <div className="pt-4 border-t border-glass-border flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <div className="flex items-start gap-2.5 text-sm">
              <Wrench size={15} className={m?.active ? 'text-status-running mt-0.5' : 'text-text-muted mt-0.5'} />
              {m?.active ? (
                <span>
                  <b>{license.edition === 'hmi' ? 'Support active' : 'Maintenance active'}</b> · {formatUsd(m.yearlyUsd)}/yr ·{' '}
                  {m.cancelAtPeriodEnd ? `ends ${formatDate(m.renewsAt)}` : `renews ${formatDate(m.renewsAt)}`}
                </span>
              ) : (
                <span className="text-text-secondary">No {plan} — the license works, but updates and priority support aren’t included.</span>
              )}
            </div>
            {canEdit && license.status === 'active' && (
              m?.active && !m.cancelAtPeriodEnd ? (
                <button type="button" disabled={busy} onClick={() => void maintenance('cancel')} className="text-xs font-semibold text-status-fault hover:underline">
                  Cancel {plan}
                </button>
              ) : (
                !m?.active && (
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => void maintenance('start')}>
                    Add {plan}
                  </Button>
                )
              )
            )}
          </div>
        )}
        {error && <Alert kind="error">{error}</Alert>}
      </div>
    </section>
  );
}

function ImportForm({ onDone }: { onDone: (notice: string | null) => void }) {
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const stored = await cloudApi.post<PortalLicense>('/api/portal/licenses/import', { key: key.trim(), label: label.trim() || undefined });
      setKey('');
      setLabel('');
      onDone(stored.registration?.message ?? null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <TextArea label="License key" required rows={3} className="font-mono text-xs" placeholder="SCADA-…" value={key} onChange={(e) => setKey(e.target.value)} />
      <TextField label="Label" placeholder="e.g. Plant 2 — Molding" value={label} onChange={(e) => setLabel(e.target.value)} />
      {error && <Alert kind="error">{error}</Alert>}
      <div className="flex justify-end">
        <Button type="submit" size="sm" disabled={busy || !key.trim()}>
          <Upload size={13} /> Store key
        </Button>
      </div>
    </form>
  );
}

export function LicensesPage() {
  usePageTitle('Licenses');
  const { session } = useSession();
  const [params, setParams] = useSearchParams();
  const { data, error, loading, reload } = useAsync(() => cloudApi.get<PortalLicense[]>('/api/portal/licenses'));
  const [importing, setImporting] = useState(false);
  const [importNotice, setImportNotice] = useState<string | null>(null);
  const checkout = params.get('checkout');
  const canEdit = session?.user.role === 'owner';

  // Stripe returns before its webhook may have landed; look again shortly.
  useEffect(() => {
    if (checkout !== 'success') return;
    const timers = [2000, 5000, 10000].map((ms) => window.setTimeout(() => void reload(), ms));
    return () => timers.forEach(window.clearTimeout);
  }, [checkout, reload]);

  const current = (data ?? []).filter((l) => (l.status === 'active' || l.status === 'pending_payment') && l.kind !== 'trial');
  const trials = (data ?? []).filter((l) => l.kind === 'trial');
  const history = (data ?? []).filter((l) => l.status === 'superseded' || l.status === 'void');

  return (
    <>
      <PortalHeader
        title="Licenses"
        description="Your appliance license keys, backed up in your account. Each key is locked to one appliance’s hardware ID."
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => setImporting(!importing)}>
              <Upload size={14} /> Store an existing key
            </Button>
            {canEdit && (
              <Button size="sm" to="/portal/licenses/new">
                <Plus size={14} /> Buy a license
              </Button>
            )}
          </>
        }
      />

      {checkout === 'success' && (
        <div className="mb-6">
          <Alert kind="success">
            Payment received. Your key is issued as soon as the payment is confirmed — usually within a few seconds.{' '}
            <button type="button" className="font-semibold underline" onClick={() => setParams({}, { replace: true })}>
              Dismiss
            </button>
          </Alert>
        </div>
      )}
      {checkout === 'cancel' && (
        <div className="mb-6">
          <Alert kind="info">Checkout was canceled. You have not been charged.</Alert>
        </div>
      )}
      {params.get('issued') && (
        <div className="mb-6">
          <Alert kind="success">License issued. Copy the key below and paste it into the appliance’s License card.</Alert>
        </div>
      )}

      {importNotice && (
        <div className="mb-6">
          <Alert kind="info">
            {importNotice}{' '}
            <button type="button" className="font-semibold underline" onClick={() => setImportNotice(null)}>
              Dismiss
            </button>
          </Alert>
        </div>
      )}
      {importing && (
        <Card title="Store an existing key" className="mb-6">
          <p className="text-sm text-text-secondary mb-4">Keep a backup of a key you received another way. The key’s signature is checked before it is stored.</p>
          <ImportForm
            onDone={(notice) => {
              setImporting(false);
              setImportNotice(notice ?? 'Key stored as a backup.');
              void reload();
            }}
          />
        </Card>
      )}

      {loading && !data ? (
        <Loading />
      ) : error ? (
        <LoadError message={error} onRetry={reload} />
      ) : current.length === 0 ? (
        <Card>
          <EmptyState
            icon={KeyRound}
            title="No licenses yet"
            body="Buy a license for an appliance, or store a key you already have so it’s backed up."
            action={
              <div className="flex flex-wrap justify-center gap-2">
                <Button to="/portal/licenses/new">Buy a license</Button>
                <Button variant="secondary" onClick={() => setImporting(true)}>
                  Store an existing key
                </Button>
              </div>
            }
          />
        </Card>
      ) : (
        <div className="space-y-5">
          {current.map((l) => (
            <LicenseCard key={l.id} license={l} canEdit={canEdit} onChanged={() => void reload()} />
          ))}
        </div>
      )}

      {trials.length > 0 && (
        <Card title="Trials" className="mt-8" padded={false}>
          <p className="px-5 pt-4 text-sm text-text-secondary">
            Trial keys run alongside your licenses on the appliance. When a trial ends, only its extra features switch off — nothing you bought is affected.
          </p>
          <ul className="divide-y divide-glass-border">
            {trials.map((t) => {
              const expired = t.status === 'expired';
              const daysLeft = t.expiresAt ? Math.max(0, Math.ceil((new Date(t.expiresAt).getTime() - Date.now()) / 86400000)) : null;
              return (
                <li key={t.id} className="px-5 py-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{t.label || (t.applianceName ?? 'Appliance trial')}</span>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${expired ? 'bg-ink-muted text-text-muted' : 'bg-brand-secondary/10 text-brand-secondary'}`}>
                      {expired ? `Ended ${formatDate(t.expiresAt)}` : `Trial · ${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}
                    </span>
                    <span className="text-xs font-mono text-text-muted">{formatHardwareId(t.hardwareId)}</span>
                  </div>
                  <div className="mt-1 text-xs text-text-secondary">
                    {t.isAll ? 'All features' : t.features.filter(isLicensableFeature).map((f) => FEATURE_METADATA[f].name).join(', ')} · until {formatDate(t.expiresAt)}
                  </div>
                  {t.key && !expired && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <code className="max-w-full sm:max-w-md truncate rounded bg-ink-muted border border-glass-border px-2 py-1 font-mono text-[11px]">{t.key}</code>
                      <CopyButton text={t.key} label="Copy trial key" />
                      {canEdit && (
                        <Button size="sm" variant="secondary" to={`/portal/licenses/new?hwid=${t.hardwareId}&features=${t.features.filter(isLicensableFeature).join(',')}`}>
                          Buy these features
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {history.length > 0 && (
        <Card
          className="mt-8"
          padded={false}
          title={
            <span className="inline-flex items-center gap-2">
              <History size={15} /> Replaced keys
            </span>
          }
        >
          <Table head={['Hardware ID', 'Features', 'Status', 'Issued', '']}>
            {history.map((l) => (
              <tr key={l.id}>
                <td className="px-5 py-2.5 font-mono text-xs">{formatHardwareId(l.hardwareId)}</td>
                <td className="px-5 py-2.5">{l.isAll ? 'All features' : `${l.features.filter(isLicensableFeature).length} features`}</td>
                <td className="px-5 py-2.5 text-text-secondary">{l.status === 'superseded' ? 'Replaced by an upgrade' : 'Void'}</td>
                <td className="px-5 py-2.5 text-text-secondary">{formatDate(l.issuedAt)}</td>
                <td className="px-5 py-2.5 text-right">{l.key && <CopyButton text={l.key} />}</td>
              </tr>
            ))}
          </Table>
          <p className="px-5 py-3 text-xs text-text-muted border-t border-glass-border">Replaced keys still activate on their appliance, but the newest key includes every feature.</p>
        </Card>
      )}
    </>
  );
}

// ── Purchase ───────────────────────────────────────────────────────────────

export function NewLicensePage() {
  usePageTitle('Buy a license');
  const navigate = useNavigate();
  const { session } = useSession();
  const [params] = useSearchParams();
  const [hwidInput, setHwidInput] = useState(params.get('hwid') ?? '');
  const [label, setLabel] = useState('');
  const [selection, setSelection] = useState<LicenseSelection>(() => {
    const s = selectionFromParams(params);
    return s.edition === 'hmi' || s.all || s.features.length ? s : { edition: 'scada', all: true, features: [], maintenance: true };
  });
  const [server, setServer] = useState<{ forKey: string; response: LicenseQuoteResponse } | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const appliances = useAsync(() => cloudApi.get<PortalAppliance[]>('/api/portal/appliances'));

  const hwid = normalizeHardwareId(hwidInput);
  const requestKey = JSON.stringify({ hwid, ...selection });

  // The cloud's quote is authoritative (it knows what this appliance already owns and the partner discount).
  useEffect(() => {
    if (!hwid) {
      setServer(null);
      setQuoteError(null);
      return;
    }
    const t = window.setTimeout(async () => {
      try {
        const response = await cloudApi.post<LicenseQuoteResponse>('/api/portal/licenses/quote', {
          hardwareId: hwid,
          features: selection.features,
          all: selection.all,
          maintenance: selection.maintenance,
          edition: selection.edition,
        });
        setServer({ forKey: requestKey, response });
        setQuoteError(null);
      } catch (err) {
        setServer(null);
        setQuoteError(errorMessage(err));
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [hwid, requestKey, selection.all, selection.features, selection.maintenance]);

  const owned = server?.response.quote.ownedFeatures ?? [];
  const localQuote = useMemo(
    () => quoteLicense({ edition: selection.edition, features: selection.features, all: selection.all, owned, discountPct: session?.account.partnerDiscountPct ?? 0, maintenance: selection.maintenance && !server?.response.maintenanceActive }),
    [selection, owned, session?.account.partnerDiscountPct, server?.response.maintenanceActive],
  );
  const fresh = server?.forKey === requestKey;
  const quote = fresh ? server!.response.quote : localQuote;

  // Registered appliances that have reported a hardware ID.
  const knownAppliances = (appliances.data ?? [])
    .map((a) => ({ name: a.name, hwid: a.latest ? normalizeHardwareId(a.latest.applianceId) : null }))
    .filter((a): a is { name: string; hwid: string } => !!a.hwid);

  async function checkout() {
    if (!hwid) return;
    const priceLine = quote.totalUsd > 0 ? `${formatUsd(quote.totalUsd)} for the license` : 'no license charge';
    const maintLine = quote.maintenanceYearlyUsd > 0 ? ` plus ${formatUsd(quote.maintenanceYearlyUsd)} for the first year of ${quote.edition === 'hmi' ? 'support' : 'maintenance'}` : '';
    if (
      session?.billing.provider === 'invoice' &&
      !window.confirm(`Issue a license for hardware ID ${formatHardwareId(hwid)} — ${priceLine}${maintLine}, billed by invoice? Licenses can't be moved to another appliance.`)
    )
      return;
    setBusy(true);
    setCheckoutError(null);
    try {
      const res = await cloudApi.post<LicenseCheckoutResponse>('/api/portal/licenses/checkout', {
        hardwareId: hwid,
        features: selection.features,
        all: selection.all,
        maintenance: selection.maintenance,
        edition: selection.edition,
        label: label.trim() || undefined,
      });
      if (res.kind === 'redirect') {
        window.location.assign(res.url);
        return;
      }
      navigate(`/portal/licenses?issued=${res.license.id}`);
    } catch (err) {
      setCheckoutError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const canBuy = !!hwid && fresh && !quote.empty && !quote.blockedReason && !quoteError && session?.user.role === 'owner';

  return (
    <>
      <Link to="/portal/licenses" className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary mb-4">
        <ArrowLeft size={14} /> Licenses
      </Link>
      <PortalHeader title="Buy a license" description="Choose the appliance, pick features, and get a perpetual key locked to that appliance." />

      {session?.account.partnerTier && session.account.partnerDiscountBlockedReason && (
        <div className="mb-6">
          <Alert kind="info">
            Your partner discount is paused: {session.account.partnerDiscountBlockedReason} Contact Scada.Ink to update your certification records.
          </Alert>
        </div>
      )}
      <Card title="1. Which appliance?" className="mb-6">
        <div className="grid md:grid-cols-2 gap-4">
          <TextField
            label="Hardware ID"
            required
            className="font-mono"
            placeholder="e.g. 1000-0000-a1b2-c3d4"
            value={hwidInput}
            onChange={(e) => setHwidInput(e.target.value)}
            hint={
              hwidInput && !hwid ? (
                <span className="text-status-fault">A hardware ID is 16 or 32 letters and numbers (0–9, a–f).</span>
              ) : (
                'On the appliance: Admin → Settings → License card → Copy hardware ID.'
              )
            }
          />
          <TextField label="Label (optional)" placeholder="e.g. Line 2 — Molding" value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>
        {knownAppliances.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-text-muted">Your registered appliances:</span>
            {knownAppliances.map((a) => (
              <button
                key={a.hwid}
                type="button"
                onClick={() => {
                  setHwidInput(formatHardwareId(a.hwid));
                  if (!label) setLabel(a.name);
                }}
                className={`px-2.5 py-1 rounded-full border font-medium ${hwid === a.hwid ? 'border-brand-primary bg-brand-primary/5 text-brand-primary' : 'border-glass-border-bright hover:border-brand-primary/40'}`}
              >
                {a.name}
              </button>
            ))}
          </div>
        )}
        {hwid && fresh && server!.response.existingLicenseId && selection.edition === 'scada' && (
          <div className="mt-4">
            <Alert kind="info">
              {server!.response.quote.ownedFeatures.length === 0
                ? `${server!.response.applianceName ?? 'This appliance'} has an HMI.ink license. Buying Scada.Ink features upgrades it — the HMI.ink price is credited and you get a Scada.Ink key that replaces it.`
                : `${server!.response.applianceName ?? 'This appliance'} already has a license with ${server!.response.quote.ownedFeatures.length} features. This purchase upgrades it: you pay only for new features and get a replacement key that includes everything.`}
            </Alert>
          </div>
        )}
        {hwid && fresh && server!.response.activeTrials.length > 0 && (
          <div className="mt-4">
            <Alert kind="info">
              This appliance is trialing{' '}
              {server!.response.activeTrials
                .map((t) => `${t.features.filter(isLicensableFeature).map((f) => FEATURE_METADATA[f].name).join(', ')} (until ${formatDate(t.expiresAt)})`)
                .join('; ')}
              . Buying those features keeps them after the trial ends.
            </Alert>
          </div>
        )}
        {quoteError && (
          <div className="mt-4">
            <Alert kind="error">{quoteError}</Alert>
          </div>
        )}
      </Card>

      <div className="grid xl:grid-cols-[1fr_340px] gap-6 items-start">
        <div>
          <h2 className="text-sm font-semibold mb-3">2. Choose features</h2>
          <FeaturePicker selection={selection} onChange={setSelection} owned={owned} />
        </div>
        <aside className="xl:sticky xl:top-24">
          <QuoteSummary
            quote={quote}
            selection={selection}
            pending={!!hwid && !fresh && !quoteError}
            maintenanceActive={!!server?.response.maintenanceActive}
            onMaintenanceChange={(m) => setSelection({ ...selection, maintenance: m })}
          >
            {!hwid && (
              <div className="mb-3 flex items-start gap-2 text-xs text-status-idle">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" /> Enter the appliance’s hardware ID to see your final price.
              </div>
            )}
            {checkoutError && (
              <div className="mb-3">
                <Alert kind="error">{checkoutError}</Alert>
              </div>
            )}
            <Button size="lg" className="w-full" disabled={!canBuy || busy} onClick={() => void checkout()} arrow>
              {busy ? 'Starting checkout…' : quote.totalUsd === 0 && !quote.empty ? 'Issue license' : session?.billing.provider === 'stripe' ? 'Pay and get key' : 'Get license key'}
            </Button>
            {session?.user.role !== 'owner' && <p className="mt-2 text-xs text-text-muted text-center">Only the account owner can buy licenses.</p>}
          </QuoteSummary>
        </aside>
      </div>
    </>
  );
}
